import { request, UnauthorizedError } from "utils/request"
import { asyncAction } from "./common"
import { MemberRoles } from "."
import { getState } from "storeAccess"

export const loggedOutState = {
  companyId: null,
  companyName: null,
  teamId: null,
  memberId: null,
  accessToken: null,
  role: null,
  isLoggedIn: false,
  isOnlyLibrarian: null,
  isMember: true, // Default true, just to be safe
  isViewOnly: false,
  // SPEC-025: dev-only — true when the BE has the LIMS GMP four-eyes + capability gates relaxed
  // locally. Default false; never true on the test/prod servers.
  gmpControlsRelaxed: false,
  globalMessage: "",
  showGlobalMessage: false,
  hasApiToken: null,
  language: null,
  plannertechLanguage: "en",
  defaultChatbot: "",
  pendingMemberships: null,
}

function loginSuccessState(res) {
  return {
    companyId: res.company && res.company.id,
    companyName: res.company && res.company.name,
    teamId: !res.team ? res.team : res.team.id,
    memberId: res.id,
    accessToken: res.access_token,
    role: res.role,
    isOnlyLibrarian: res.isOnlyLibrarian,
    isLoggedIn: true,
    isMember: res.role === MemberRoles.MEMBER,
    isViewOnly: res.role === MemberRoles.VIEW_ONLY,
    gmpControlsRelaxed: res.gmpControlsRelaxed ?? false,
    globalMessage: res.globalMessage,
    showGlobalMessage: res.globalMessage ? true : false,
    hasApiToken: res.hasApiToken !== undefined ? res.hasApiToken : null,
    language: res.language,
    plannertechLanguage: res.plannertechLanguage ?? "en",
    defaultChatbot: res.defaultChatbotId,
    pendingMemberships: null,
  }
}

export function reducer(state = loggedOutState, action) {
  // A bit out of the ordinary reducer-stuff:
  // Log the user out if we receive such a status from the BE.
  // This works regardless of whether the user is currently logged in or not!
  if (action.err instanceof UnauthorizedError) {
    return loggedOutState
  }

  const res = action.res
  switch (action.type) {
    case "loading/fetchAll/ERROR":
      // Log out if fetching everything failed
      // This is done to prevent infinite loops!
      // But don't logout if it's an AbortError (page refresh)
      if (action.err && action.err.name === 'AbortError') {
        return state
      }
      return loggedOutState
    case "auth/login":
      if (res && res.requires_team_selection) {
        return { ...loggedOutState, pendingMemberships: res.memberships }
      }
      return loginSuccessState(res)
    case "auth/verifyMfaToken":
    case "auth/logout-other-device":
    case "auth/selectTeam":
      if (res && res.requires_team_selection) {
        return { ...loggedOutState, pendingMemberships: res.memberships }
      }
      return loginSuccessState(res)
    case "auth/accessCompany":
      return { ...state, companyId: res.id, companyName: res.name }
    case "auth/changeShowGlobalMessage":
      return { ...state, showGlobalMessage: false }
    case "auth/exitCompany":
      return { ...state, companyId: null }
    case "auth/clearPendingMemberships":
      return loggedOutState
    case "auth/logout":
      return loggedOutState
    case "auth/setGlobalMessage":
      return { ...state, globalMessage: action.args[0] }
    case "auth/setHasAPIToken":
      return { ...state, hasApiToken: action.hasApiToken }
    case "auth/setLanguage":
      return { ...state, language: action.args[0] }
    case "auth/setPlannertechLanguage":
      return { ...state, plannertechLanguage: action.plannertechLanguage }
    case "auth/setRole":
      return {
        ...state,
        role: action.role,
        isMember: action.role === MemberRoles.MEMBER,
        isViewOnly: action.role === MemberRoles.VIEW_ONLY,
      }
    case "auth/setDefaultChatbot":
      return { ...state, defaultChatbot: action.args[0] }
    default:
      return state
  }
}

export const login = asyncAction("auth/login", (dispatch, email, password) => {
  return request("POST", `/login`, { email, password }, true, true)
})

export const selectTeam = asyncAction("auth/selectTeam", (dispatch, email, password, memberId) => {
  return request("POST", `/login/select-team`, { email, password, memberId }, true, true)
})

export const logout = asyncAction("auth/logout", (_dispatch) => {
  const state = getState()
  window.localStorage.setItem("plannertechVersion", JSON.stringify(state.teamStatus.plannertechVersion))
  return request("POST", `/logout`).then(() => {
    return { type: "auth/logout" }
  })
})

export const logoutOtherDevice = asyncAction("auth/logout-other-device", (dispatch, email, password) => {
  return request("POST", `/logout-other-device`, { email, password }, true, true)
})

export const accessCompany = asyncAction("auth/accessCompany", (dispatch, id, name) => {
  return request("PATCH", `/customer-admin/give-company-id/${id}`).then(() => ({ id, name }))
})

export const exitTeam = asyncAction("auth/exitCompany", (_dispatch) => {
  return request("PATCH", `/customer-admin/remove-company-id`)
})

export const getMfaToken = asyncAction("auth/getMfaToken", (dispatch, email, password) => {
  return request("POST", `/get-mfa-key`, { email, password })
})

// SPEC-057 L2 — self-service password reset ("forgot password" on the login screen).
// Unauthenticated, login-family cookie handling. The backend ALWAYS answers 200 (no user
// enumeration) — the FE shows a neutral "if the account exists, a code was sent" either way.
export const requestPasswordReset = asyncAction("auth/requestPasswordReset", (dispatch, email) => {
  return request("POST", `/request-password-reset`, { email }, true, true)
})

// Sets a new password from the emailed 6-digit code (reset or welcome — same endpoint).
export const setPassword = asyncAction("auth/setPassword", (dispatch, email, code, newPassword) => {
  return request("POST", `/set-password`, { email, code, newPassword }, true, true)
})

export const verifyMfaToken = asyncAction("auth/verifyMfaToken", (dispatch, email, password, mfaKey) => {
  return request("POST", `/verify-mfa-key`, { email, password, mfaKey }, true, true)
})

export function clearPendingMemberships() {
  return { type: "auth/clearPendingMemberships" }
}

// SPEC-056 UI-review: end the session locally after a successful self-service password/email
// change. The backend stamps last_logout, so the next request would 401 with the misleading
// "You have been logged out by another device" — instead the caller shows a proper explanation
// and this lands the user on the login screen immediately.
export function endSession() {
  return { type: "auth/logout" }
}

export function changeShowGlobalMessage() {
  return { type: "auth/changeShowGlobalMessage" }
}

export const setGlobalMessage = asyncAction("auth/setGlobalMessage", (dispatch, message) => {
  return request("POST", `/customer-admin/set-global-message`, { message })
})

export function setHasAPIToken(hasApiToken) {
  return { type: "auth/setHasAPIToken", hasApiToken }
}

export const setLanguage = asyncAction("auth/setLanguage", (dispatch, language) => {
  return request("PATCH", `/librarian/speech-to-text/language`, { input: { language: language } })
})

export function setPlannertechLanguage(lang) {
  return { type: "auth/setPlannertechLanguage", plannertechLanguage: lang }
}

// #4124 — a role change on your OWN member takes effect at once.
//
// The role was only ever set from the login response, so changing your own role left the whole
// interface behaving as the old one until you logged out and in again: colleagues' settings still
// opened, planner-only controls stayed visible, and the server refused the actions they offered.
// The server itself was never wrong — it reads the role from the database on every request — so
// this cached copy was the entire bug. Every consumer reads it reactively, so updating it here is
// enough; there is nothing to fix screen by screen.
//
// MIND THE TWO SPELLINGS. The login response carries the enum VALUE ("planner"), which is what
// this slice holds and what every `auth.role === "planner"` comparison expects. A member dump
// carries the enum NAME ("PLANNER"), which is what the member forms use. A role change arrives
// from a member dump, so it is normalised here — crossing the two conventions would silently
// break every role check in the app.
export function setRole(memberDumpRole) {
  return { type: "auth/setRole", role: String(memberDumpRole).toLowerCase() }
}

export const setDefaultChatbot = asyncAction("auth/setDefaultChatbot", (dispatch, defaultChatbotId) => {
  return request("PATCH", `/librarian/chatbot/default-chatbot`, { patch: { defaultChatbotId } })
})
